import {
    DotLegalButton,
    DotLegalCompletedPercentage,
    DotLegalEmptyState,
    DotLegalLink,
    DotLegalMultiSelect,
    DotLegalOverflowMenu,
    DotLegalPageHeader,
    DotLegalPaper,
    DotLegalSelect,
    DotLegalTable,
    DotLegalTooltip,
    ITableHeader,
} from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { useUrlProvider } from "../../../useUrlProvider";
import PageHeaderEditEntityButton, {
    PageHeaderEditEntityButtonMenuItem,
} from "../../../common/components/pageHeaderEditEntityButton/PageHeaderEditEntityButton";
import { Box, TableCell, TableRow } from "@mui/material";
import useFrameworkControlOverviewHooks from "./FrameworkControlOverview.hooks";
import DeleteFrameworkControlDialog from "../../dialogs/deleteFrameworkControlDialog/DeleteFrameworkControlDialog";
import { useLocation, useNavigate } from "react-router";
import CreateEditFrameworkControlDialog from "../createFrameworkControlDialog/CreateEditFrameworkControlDialog";
import React, { createElement, useState } from "react";
import { FrameworkControlTaskTableRow } from "./FrameworkControlOverview.types";
import FrameworkControlStatusChip from "../../frameworkOverview/frameworkControlStatusChip/FrameworkControlStatusChip";
import { Trans } from "react-i18next";
import { isNullOrWhitespace } from "../../../common/stringOperations";
import FilterBar from "@common/components/filterBar/FilterBar.tsx";
import { useRecurringIntervalFormatting } from "../../../annualWheel/AnnualWheel.hooks";
import AnnualWheelActivitySelectorDialog from "./annualWheelActivitySelectorDialog/AnnualWheelActivitySelectorDialog.tsx";
import EmptyStateSvg from "../../frameworkEmptyState.svg?react";
import DeleteFrameworkControlTaskDialog from "../deleteFrameworkControlTaskDialog/DeleteFrameworkControlTaskDialog";
import { sortIds } from "../../../common/utilities.ts";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import AnchoredPoliciesDialog from "./anchoredPoliciesDialog/AnchoredPoliciesDialog.tsx";
import LinkedRiskAssessmentsDialog from "./linkedRiskAssessmentsDialog/LinkedRiskAssessmentsDialog.tsx";
import QuestionnaireEvaluation from "../../../common/components/questionnaireComponents/questionnaireEvaluation/QuestionnaireEvaluation.tsx";
import { EntityType } from "../../../common/Common.types.ts";
import FrameworkControlAnnualWheelActivitySelectorDialog from "./frameworkControlAnnualWheelActivitySelectorDialog/FrameworkControlAnnualWheelActivitySelectorDialog.tsx";
import MentionInputRender from "@common/components/mentionInput/MentionInputRender.tsx";
import EntityDetailTopBar from "@common/components/entityDetailTopBar/EntityDetailTopBar";
import TopBarRow from "@common/components/entityDetailTopBar/TopBarRow";
import TopBarNoteTile from "@common/components/entityDetailTopBar/TopBarNoteTile";
import TopBarTextTile from "@common/components/entityDetailTopBar/TopBarTextTile";
import TopBarNumberTile from "@common/components/entityDetailTopBar/TopBarNumberTile";

function FrameworkControlOverview() {
    const navigate = useNavigate();
    const location = useLocation();
    const formatRecurringInterval = useRecurringIntervalFormatting();
    const { permissions } = useUserContext();
    const { platformFeatures, addonsFeatures } = usePlanContext();
    const hasPolicyManagement = addonsFeatures.policyManagement;
    const { translateString, translateFrameworkControlStatusEnum, translateAnnualWheelActivityGeneralTypeEnum, translateDate } = useTranslation();
    const { getFrameworksUrl, getFrameworkOverviewUrl } = useUrlProvider();
    const [showPolicyDialog, setShowPolicyDialog] = useState(false);
    const [showRiskAssessmentsDialog, setShowRiskAssessmentsDialog] = useState(false);
    const {
        id,
        data,
        isLoading,
        setShowDeleteDialog,
        setShowEditDialog,
        showDeleteDialog,
        showEditDialog,
        refetchOverview,
        openActivity,
        responsibleOptions,
        responsiblesIsLoading,
        rows,
        getFilterDataOptions,
        onFiltersChanged,
        clearFilters,
        hasActiveFilters,
        searchString,
        setSearchString,
        onAssigneesChange,
        onResponsibleChange,
        hasControls,
        showAddAnnualWheelActivityDialog,
        setShowAddAnnualWheelActivityDialog,
        setTaskForDeletion,
        taskForDeletion,
        showAddFrameworkControlTemplateAnnualWheelActivity,
        setShowAddFrameworkControlTemplateAnnualWheelActivity,
        onAddedAnnualWheelActivityTemplates,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useFrameworkControlOverviewHooks();

    const getTaskLinkUrl = (taskId: string) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("taskId", taskId);
        return `?${searchParams.toString()}`;
    };

    function getMenuItems() {
        let menuItems: Array<PageHeaderEditEntityButtonMenuItem> = [];

        if (permissions.frameworkPermissions.edit) {
            menuItems.push({ menuItem: translateString("edit"), onClick: () => setShowEditDialog(true) });
            menuItems.push({ menuItem: translateString("delete"), onClick: () => setShowDeleteDialog(true) });
        }

        return menuItems;
    }

    function getOverFlowMenuItems(row: FrameworkControlTaskTableRow) {
        let menuItems = [];

        if (permissions.taskPermissions.read) {
            menuItems.push({
                menuItem: translateString("openTask"),
                onClick: () => navigate(getTaskLinkUrl(row.taskId)),
            });
        }

        if (permissions.annualWheelPermission.edit) {
            menuItems.push({
                menuItem: translateString("editActivity"),
                onClick: () => openActivity(row.annualWheelActivityId),
            });
        }

        if (permissions.frameworkPermissions.edit) {
            menuItems.push({
                menuItem: translateString("deleteFromControl"),
                onClick: () => setTaskForDeletion(row),
            });
        }

        return menuItems;
    }

    function getHeaders() {
        let headers = Array<ITableHeader<FrameworkControlTaskTableRow>>();
        headers.push({
            property: "taskCustomId",
            text: translateString("id"),
            sortOverride: (a, b, order) => sortIds(a.taskCustomId, b.taskCustomId, order === "desc"),
        });
        headers.push({ property: "taskName", text: translateString("task") });
        headers.push({ property: "deadlineAsString", text: translateString("deadline") });
        headers.push({ property: "annualWheelActivityGeneralType", text: translateString("type") });
        headers.push({ property: "groupEntity", text: translateString("groupEntity") });
        headers.push({ property: "responsibleAsString", text: translateString("responsible") });
        headers.push({ property: "assigneesAsString", text: translateString("assigned") });
        headers.push({ property: "recurringInterval", text: translateString("recurrence") });
        headers.push({ property: "statusAsString", text: translateString("status") });
        headers.push({ property: "evaluationStatusAsString", text: translateString("evaluation") });

        return headers;
    }

    function getTopBarTiles() {
        const tiles: Array<React.ReactNode> = [
            <TopBarTextTile key="category" label={translateString("category")} value={data?.category.name} isLoading={isLoading} />,
            <TopBarTextTile key="controlCoordinator" label={translateString("controlCoordinator")} value={data?.coordinator.name} isLoading={isLoading} />,
        ];

        if (hasPolicyManagement) {
            tiles.push(
                <TopBarNumberTile
                    key="anchoredInPolicies"
                    label={translateString("anchoredInPolicies")}
                    value={data?.policies.length ?? 0}
                    onClick={data && data.policies.length > 0 ? () => setShowPolicyDialog(true) : undefined}
                    isLoading={isLoading}
                />
            );
        }

        if (permissions.canAccessEnterpriseRiskManagement) {
            tiles.push(
                <TopBarNumberTile
                    key="linkedRiskAssessments"
                    label={translateString("linkedRiskAssessments")}
                    value={data?.riskAssessments.length ?? 0}
                    onClick={data && data.riskAssessments.length > 0 ? () => setShowRiskAssessmentsDialog(true) : undefined}
                    isLoading={isLoading}
                />
            );
        }

        if (!isNullOrWhitespace(data?.implementationGroup)) {
            tiles.push(
                <TopBarTextTile
                    key="implementationGroup"
                    label={translateString("implementationGroup")}
                    value={data?.implementationGroup}
                    isLoading={isLoading}
                />
            );
        }

        tiles.push(
            <TopBarTextTile
                key="status"
                label={translateString("status")}
                value={
                    data ? (
                        <FrameworkControlStatusChip
                            isExcluded={false}
                            status={data.status}
                            value={translateFrameworkControlStatusEnum(data.status.toString())}
                        />
                    ) : undefined
                }
                isLoading={isLoading}
            />
        );

        // Tags moves onto the second row (ahead of Note and Control progress) so the first row keeps the
        // identity + linkage tiles together, including the new linked-risk-assessments count.
        if (platformFeatures.tags && permissions.canAccessTags) {
            tiles.push(<TopBarTextTile key="tags" label={translateString("tags")} value={data?.tags.map((x) => x.name).join(", ")} isLoading={isLoading} />);
        }

        tiles.push(
            <TopBarNoteTile
                key="note"
                onNoteUpdated={() => refetchOverview()}
                postUrl={`/frameworkcontrols/${id!}/note`}
                note={data?.note}
                disabled={!permissions.frameworkPermissions.edit}
                entityId={id}
                entityType={EntityType.FrameworkControl}
                isLoading={isLoading}
            />,
            <TopBarTextTile
                key="controlProgress"
                label={translateString("controlProgress")}
                value={
                    <DotLegalTooltip
                        text={createElement(Trans, {
                            i18nKey: "frameworkControlCompletedTasks",
                            values: { completedTasks: data?.numberOfTasksCompleted, allTasks: data?.numberOfTotalTasks },
                        })}
                    >
                        <span>
                            <DotLegalCompletedPercentage completed={data?.progress ?? 0} />
                        </span>
                    </DotLegalTooltip>
                }
                isLoading={isLoading}
            />
        );

        return tiles;
    }

    const topBarTiles = getTopBarTiles();
    const firstRowTiles = topBarTiles.slice(0, 6);
    const secondRowTiles = topBarTiles.slice(6);

    return (
        <>
            <DotLegalPageHeader
                breadCrumbs={[
                    { name: translateString("frameworks"), link: getFrameworksUrl() },
                    {
                        name: data?.frameworkInfo.name,
                        tooltip: data?.frameworkInfo.description,
                        link: data?.frameworkInfo.id ? getFrameworkOverviewUrl(data.frameworkInfo.id) : undefined,
                    },
                    { name: data?.customId ? data?.customId + " " + data?.name : data?.name, tooltip: data?.description },
                ]}
                rightComponent={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <PageHeaderEditEntityButton menuItems={getMenuItems()} />
                    </Box>
                }
            />
            <EntityDetailTopBar>
                <TopBarRow>{firstRowTiles}</TopBarRow>
                {secondRowTiles.length > 0 && <TopBarRow>{secondRowTiles}</TopBarRow>}
            </EntityDetailTopBar>
            <DotLegalPaper sx={{ marginTop: 2 }}>
                <Box>
                    {hasControls ? (
                        <>
                            <FilterBar
                                isLoading={isLoading}
                                filterData={getFilterDataOptions()}
                                fetchFilterData
                                onMenuClose={(options) => onFiltersChanged(options)}
                                onRemove={(options) => onFiltersChanged(options)}
                                pinnedFilterIds={pinnedFilterIds}
                                onPinnedFilterIdsChange={onPinnedFilterIdsChange}
                                searchValue={searchString}
                                searchLabel={translateString("search")}
                                onSearchChange={(value) => setSearchString(value)}
                                onClearFilter={hasActiveFilters ? () => clearFilters() : undefined}
                                clearFilterText={hasActiveFilters ? translateString("clearFilter") : undefined}
                            />
                            <DotLegalTable
                                headers={getHeaders()}
                                rowsPerPage={30}
                                defaultOrderBy={"taskName"}
                                isLoading={isLoading}
                                emptyText={translateString("noTasksMatchesQuery")}
                                noSearchResultsText={translateString("noSearchResultsText")}
                                paginationLabelOf={translateString("labelOf")}
                                searchString={searchString}
                                data={rows}
                                clickableRows={false}
                                hasOverflowMenu
                                renderRow={(row) => {
                                    return (
                                        <TableRow key={row.taskId}>
                                            <TableCell>{row.taskCustomId}</TableCell>
                                            <TableCell sx={(theme) => ({ minWidth: 300, "& button": { fontSize: theme.typography.pxToRem(12) } })} align="left">
                                                {permissions.taskPermissions.read ? (
                                                    <DotLegalLink stopPropagation leftAlign bold linkColor={"primary"} to={getTaskLinkUrl(row.taskId)}>
                                                        {row.taskName}
                                                    </DotLegalLink>
                                                ) : (
                                                    row.taskName
                                                )}
                                            </TableCell>
                                            <TableCell>{translateDate(row.deadline)}</TableCell>
                                            <TableCell>
                                                {row.annualWheelActivityGeneralType &&
                                                    translateAnnualWheelActivityGeneralTypeEnum(row.annualWheelActivityGeneralType.toString())}
                                            </TableCell>
                                            <TableCell>{isNullOrWhitespace(row.groupEntity) ? translateString("entireGroup") : row.groupEntity}</TableCell>
                                            <TableCell
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                                width={250}
                                            >
                                                <Box
                                                    sx={(theme) => ({
                                                        marginRight: 1,
                                                        "& .MuiAutocomplete-root .MuiInputBase-input": {
                                                            marginBottom: -1,
                                                            fontSize: theme.typography.pxToRem(12),
                                                        },
                                                    })}
                                                >
                                                    <DotLegalSelect
                                                        variant={"standard"}
                                                        isLoading={responsiblesIsLoading}
                                                        options={responsibleOptions?.filter((x) => !row.assignees.some((a) => a.id === x.id))}
                                                        onChange={(responsible) => onResponsibleChange(row.taskId, responsible!)}
                                                        placeholder={translateString("responsible")}
                                                        noOptionsLabel={translateString("noOptions")}
                                                        selectedItem={row.responsible?.id}
                                                        label={""}
                                                        disableClearable
                                                        noMargin
                                                        disabled={!permissions.taskPermissions.edit}
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                                width={300}
                                            >
                                                <Box
                                                    sx={(theme) => ({
                                                        marginRight: 1,
                                                        "& .MuiAutocomplete-root .MuiInputBase-input": {
                                                            marginBottom: -1,
                                                            fontSize: theme.typography.pxToRem(12),
                                                        },
                                                    })}
                                                >
                                                    <DotLegalMultiSelect
                                                        variant={"standard"}
                                                        isLoading={responsiblesIsLoading}
                                                        options={responsibleOptions?.filter((x) => x.id !== row.responsible?.id)}
                                                        onChange={(values) => onAssigneesChange(row.taskId, values)}
                                                        placeholder={row.responsible?.name ?? translateString("assignTo")}
                                                        noOptionsLabel={translateString("noOptions")}
                                                        chosenOptions={row.assignees.map((x) => x.id)}
                                                        label={""}
                                                        disableClearable
                                                        noMargin
                                                        limit={1}
                                                        disabled={!permissions.taskPermissions.edit}
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell>{formatRecurringInterval(row.recurringInterval, row.recurringIntervalType)}</TableCell>
                                            <TableCell>
                                                <FrameworkControlStatusChip isExcluded={false} status={row.status} value={row.statusAsString} />
                                            </TableCell>
                                            <TableCell>
                                                <QuestionnaireEvaluation
                                                    evaluation={<MentionInputRender value={row.evaluation ?? ""} linkColor={"white"} />}
                                                    evaluationStatus={row.evaluationStatus}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <DotLegalOverflowMenu items={getOverFlowMenuItems(row)} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                }}
                            />
                        </>
                    ) : (
                        <DotLegalEmptyState icon={<EmptyStateSvg />} text={translateString("frameworkControlTaskEmptyState")} />
                    )}
                </Box>
                {permissions.frameworkPermissions.edit && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        {data && data.createdFromFrameworkControlId && (
                            <DotLegalButton onClick={() => setShowAddFrameworkControlTemplateAnnualWheelActivity(true)} buttonType={"primary"}>
                                {translateString("addTaskFromControlTemplate")}
                            </DotLegalButton>
                        )}
                        <DotLegalButton onClick={() => setShowAddAnnualWheelActivityDialog(true)} buttonType={"primary"}>
                            {translateString("addTask")}
                        </DotLegalButton>
                    </Box>
                )}
            </DotLegalPaper>
            {showPolicyDialog && data && <AnchoredPoliciesDialog policies={data.policies} onDialogClose={() => setShowPolicyDialog(false)} />}
            {showRiskAssessmentsDialog && data && (
                <LinkedRiskAssessmentsDialog riskAssessments={data.riskAssessments} onDialogClose={() => setShowRiskAssessmentsDialog(false)} />
            )}
            {showDeleteDialog && (
                <DeleteFrameworkControlDialog
                    controlId={id!}
                    controlName={data!.name}
                    onDialogClose={() => setShowDeleteDialog(false)}
                    onDeleted={() => {
                        navigate(getFrameworkOverviewUrl(data!.frameworkInfo.id));
                    }}
                />
            )}
            {taskForDeletion && (
                <DeleteFrameworkControlTaskDialog
                    controlId={id!}
                    selectedControlTask={taskForDeletion}
                    onDialogClose={() => setTaskForDeletion(undefined)}
                    onDeleted={() => {
                        refetchOverview();
                        setTaskForDeletion(undefined);
                    }}
                />
            )}
            {showEditDialog && (
                <CreateEditFrameworkControlDialog
                    frameworkId={data!.frameworkInfo.id}
                    onCloseDialog={() => {
                        setShowEditDialog(false);
                    }}
                    onSaved={(id) => {
                        refetchOverview();
                        setShowEditDialog(false);
                    }}
                    selectedControl={{
                        categoryId: data?.category.id,
                        coordinatorId: data?.coordinator.id,
                        customId: data?.customId,
                        name: data?.name,
                        description: data?.description,
                        id: id,
                        tagIds: data?.tags.map((x) => x.id) ?? [],
                        implementationGroup: data?.implementationGroup,
                    }}
                />
            )}
            {showAddAnnualWheelActivityDialog && (
                <AnnualWheelActivitySelectorDialog
                    controlId={id!}
                    onCloseDialog={() => {
                        setShowAddAnnualWheelActivityDialog(false);
                    }}
                    onAnnualWheelActivitiesSaved={() => {
                        refetchOverview();
                        setShowAddAnnualWheelActivityDialog(false);
                    }}
                />
            )}
            {showAddFrameworkControlTemplateAnnualWheelActivity && (
                <FrameworkControlAnnualWheelActivitySelectorDialog
                    controlTemplateId={data?.createdFromFrameworkControlId!}
                    controlId={id!}
                    onCloseDialog={() => setShowAddFrameworkControlTemplateAnnualWheelActivity(false)}
                    onAnnualWheelActivitiesSaved={onAddedAnnualWheelActivityTemplates}
                    frameworkGroupEntityIds={data?.frameworkInfo.groupEntities ?? []}
                />
            )}
        </>
    );
}

export default FrameworkControlOverview;
